import React, { useState } from 'react';
import { Phone, Building2, ArrowRight, CheckCircle2, X, Shield, AlertCircle, DollarSign, Clock } from 'lucide-react';
import { submitLoanApplication, testDatabaseConnection } from '../lib/formService';
import { useLocation } from 'react-router-dom';

interface LoanOnboardingProps {
  isOpen: boolean;
  onClose: () => void;
  loanAmount: number;
  loanTerm: number;
}

const banks = [
  {
    id: 'akbank',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png'
  },
  {
    id: 'yapikredi',
    name: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png'
  },
  {
    id: 'garanti',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg'
  },
  {
    id: 'isbank',
    name: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png'
  },
  {
    id: 'qnb',
    name: 'QNB Finansbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png'
  }
];

const steps = [
  {
    title: 'İletişim Bilgileri',
    description: 'Kredi teklifleri için iletişim bilgilerinizi girin'
  },
  {
    title: 'Banka Seçimi',
    description: 'Kredi tekliflerini almak istediğiniz bankaları seçin'
  },
  {
    title: 'Tamamlandı',
    description: 'Teklifler SMS ile gönderilecek'
  }
];

const LoanOnboarding: React.FC<LoanOnboardingProps> = ({
  isOpen,
  onClose,
  loanAmount,
  loanTerm
}) => {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [selectedBanks, setSelectedBanks] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionTested, setConnectionTested] = useState(false);
  const [errors, setErrors] = useState<{
    phone?: string;
    consent?: string;
    banks?: string;
    submit?: string;
    connection?: string;
  }>({});

  // Test database connection when modal opens - moved before early return
  React.useEffect(() => {
    if (isOpen && !connectionTested) {
      testDatabaseConnection().then(result => {
        setConnectionTested(true);
        if (!result.success) {
          setErrors({ connection: 'Veritabanı bağlantısı başarısız. Lütfen daha sonra tekrar deneyin.' });
        }
      });
    }
  }, [isOpen, connectionTested]);

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
      // Clear phone error when user starts typing
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: undefined }));
      }
    }
  };

  const formatPhone = (value: string) => {
    if (!value) return '';
    const match = value.match(/^(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (!match) return value;
    return match
      .slice(1)
      .filter(Boolean)
      .join(' ');
  };

  const handleBankToggle = (bankId: string) => {
    setSelectedBanks(prev => 
      prev.includes(bankId)
        ? prev.filter(id => id !== bankId)
        : [...prev, bankId]
    );
    // Clear banks error when user selects a bank
    if (errors.banks) {
      setErrors(prev => ({ ...prev, banks: undefined }));
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      maximumFractionDigits: 0
    }).format(value);
  };

  const validateStep = () => {
    const newErrors: typeof errors = {};

    if (step === 1) {
      if (!phone || phone.length < 10) {
        newErrors.phone = 'Lütfen geçerli bir telefon numarası girin (10 haneli)';
      }
      if (!consent) {
        newErrors.consent = 'Devam etmek için KVKK metnini onaylamanız gerekmektedir';
      }
    } else if (step === 2 && selectedBanks.length === 0) {
      newErrors.banks = 'Lütfen en az bir banka seçin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (step < 3) {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsLoading(false);
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    console.log('=== HANDLE SUBMIT CALLED ===');
    
    if (!validateStep()) {
      console.log('Validation failed');
      return;
    }

    if (errors.connection) {
      setErrors(prev => ({ ...prev, submit: 'Veritabanı bağlantısı sorunu. Lütfen sayfayı yenileyip tekrar deneyin.' }));
      return;
    }

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: undefined }));

    try {
      console.log('Submitting loan application with data:', {
        amount: loanAmount,
        term_months: loanTerm,
        phone_number: phone,
        selected_banks: selectedBanks,
        kvkk_consent: consent,
        marketing_consent: marketingConsent,
        source_page: location.pathname,
        source_section: 'loan_calculator_modal'
      });

      const result = await submitLoanApplication({
        amount: loanAmount,
        term_months: loanTerm,
        phone_number: phone,
        selected_banks: selectedBanks,
        kvkk_consent: consent,
        marketing_consent: marketingConsent,
        source_page: location.pathname,
        source_section: 'loan_calculator_modal'
      });

      console.log('Submission result:', result);

      if (result.success) {
        console.log('Submission successful, moving to step 3');
        setStep(3);
      } else {
        console.log('Submission failed:', result.error);
        setErrors({ submit: result.error || 'Bir hata oluştu. Lütfen tekrar deneyin.' });
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Talep Edilen Kredi Bilgileri
              </h2>
            </div>

            <div className="bg-gradient-to-br from-[#F7BA45]/10 to-slate-50 rounded-xl p-6 border border-[#F7BA45]/20">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-500 mb-2">
                    <DollarSign size={16} className="text-[#F7BA45]" />
                    Kredi Tutarı
                  </div>
                  <div className="text-2xl font-bold text-slate-800">{formatCurrency(loanAmount)}</div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-500 mb-2">
                    <Clock size={16} className="text-[#F7BA45]" />
                    Vade
                  </div>
                  <div className="text-2xl font-bold text-slate-800">{loanTerm} Ay</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Phone Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Telefon Numarası *
                </label>
                <div className="flex gap-2">
                  <div className="w-20">
                    <div className="flex items-center h-14 px-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 font-medium">
                      +90
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                      <input
                        type="text"
                        value={formatPhone(phone)}
                        onChange={handlePhoneChange}
                        placeholder="(___) ___ __ __"
                        className={`w-full h-14 pl-12 pr-4 text-lg tracking-wide border rounded-xl focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                          errors.phone ? 'border-red-300 bg-red-50' : 'border-slate-200'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Consent Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={consent}
                        onChange={e => {
                          setConsent(e.target.checked);
                          if (errors.consent) {
                            setErrors(prev => ({ ...prev, consent: undefined }));
                          }
                        }}
                      />
                      <CheckCircle2 
                        className={`${consent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    <a href="#" className="text-[#F7BA45] hover:text-[#e5ac3d] font-medium">KVKK Aydınlatma Metni</a>'ni okudum ve onaylıyorum. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex items-start">
                    <div className="bg-white border-2 rounded-lg w-6 h-6 flex flex-shrink-0 justify-center items-center
                      focus-within:border-[#F7BA45] transition-colors">
                      <input
                        type="checkbox"
                        className="opacity-0 absolute"
                        checked={marketingConsent}
                        onChange={e => setMarketingConsent(e.target.checked)}
                      />
                      <CheckCircle2 
                        className={`${marketingConsent ? 'block' : 'hidden'} h-4 w-4 text-[#F7BA45] pointer-events-none`}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 transition-colors">
                    Kampanya ve fırsatlardan haberdar olmak istiyorum.
                  </span>
                </label>
              </div>
              
              {errors.consent && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {errors.consent}
                </p>
              )}

              {errors.connection && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.connection}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Bankaları Seçin
              </h2>
              <p className="text-slate-600">
                Kredi tekliflerini almak istediğiniz bankaları seçin
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {banks.map((bank) => (
                <button
                  key={bank.id}
                  onClick={() => handleBankToggle(bank.id)}
                  className={`relative p-6 rounded-xl border-2 transition-all duration-300 group ${
                    selectedBanks.includes(bank.id)
                      ? 'border-[#F7BA45] bg-[#F7BA45]/5 shadow-lg'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {selectedBanks.includes(bank.id) && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle2 size={20} className="text-[#F7BA45]" />
                    </div>
                  )}
                  <img 
                    src={bank.logo} 
                    alt={bank.name}
                    className="h-10 object-contain mx-auto mb-3 transition-transform group-hover:scale-110"
                  />
                  <div className="font-medium text-slate-700 text-center">
                    {bank.name}
                  </div>
                </button>
              ))}
            </div>
            
            {errors.banks && (
              <p className="text-sm text-red-500 text-center flex items-center justify-center gap-1">
                <AlertCircle size={14} />
                {errors.banks}
              </p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="text-center space-y-8">
            <div className="w-20 h-20 bg-[#F7BA45]/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={40} className="text-[#F7BA45]" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">
                Teşekkürler!
              </h2>
              <p className="text-slate-600 max-w-sm mx-auto">
                Seçtiğiniz bankaların kredi teklifleri en kısa sürede SMS olarak cep telefonunuza gönderilecektir.
              </p>
            </div>

            <button
              onClick={onClose}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Tamam
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 p-2 hover:bg-slate-100 rounded-full transition-colors z-10"
          >
            <X size={20} className="text-slate-500" />
          </button>

          {/* Progress Steps */}
          <div className="absolute top-0 left-0 right-0 p-6">
            <div className="flex items-center justify-center gap-2">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center">
                  <div className="relative flex flex-col items-center">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        step > i + 1 
                          ? 'bg-[#F7BA45] text-white scale-110'
                          : step === i + 1
                            ? 'bg-[#F7BA45] text-white scale-110 ring-4 ring-[#F7BA45]/20'
                            : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {step > i + 1 ? (
                        <CheckCircle2 size={20} />
                      ) : (
                        <span className="text-base font-medium">{i + 1}</span>
                      )}
                    </div>
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                      <span className={`text-xs font-medium ${
                        step === i + 1 ? 'text-[#F7BA45]' : 'text-slate-500'
                      }`}>
                        {s.title}
                      </span>
                    </div>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="w-20 h-1 mx-2">
                      <div className="h-full bg-slate-100 rounded-full">
                        <div 
                          className="h-full bg-[#F7BA45] rounded-full transition-all duration-500"
                          style={{ width: step > i + 1 ? '100%' : '0%' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8 pt-28">
            {renderStep()}

            {/* Navigation */}
            {step < 3 && (
              <div className="mt-8">
                <button
                  onClick={step === 2 ? handleSubmit : handleNext}
                  disabled={isLoading || isSubmitting || !!errors.connection}
                  className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] disabled:bg-slate-300 text-slate-900 font-medium h-14 rounded-xl 
                    transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
                >
                  {(isLoading || isSubmitting) ? (
                    <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {step === 2 ? 'Teklifleri Al' : 'Devam Et'}
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                {/* Error Messages */}
                {errors.submit && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle size={16} />
                      <span>{errors.submit}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanOnboarding;